// One holiday decision of a payroll in words (Holiday Calendar Phase C). The server decides every amount, whether a
// day is calculated or needs a person, and whether a reference amount is a government minimum or only information;
// this file says it plainly and never works out pay.

import { shortDay as calendarShortDay } from "../../../components/calendar/calendarDays";

const CLASS_WORD = {
  REGULAR_HOLIDAY: "Regular holiday",
  SPECIAL_NON_WORKING: "Special non-working day",
  SPECIAL_WORKING: "Special working day",
};

// Shown under the "Holiday pay" earnings line wherever a payslip is read.
export const HOLIDAY_PAY_NOTE = "Pay for holidays in this period, on top of regular pay.";
export const HOLIDAY_AUTO_NOTE = "Calculated from the official calendar and the holiday pay rules.";
export const HOLIDAY_SCOPE_NOTE = "Holiday pay is calculated automatically only for employees whose holiday pay is set to the standard rules. Overtime on a holiday, company-specific holiday benefits and local holidays are not calculated automatically; a person decides those days.";
export const HOLIDAY_STALE_MESSAGE = "Payroll changed since you opened this review. Refresh to see the current holiday decision.";

export const duration = (value) => {
  const total = Number(value || 0);
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (!hours) return `${rest} min`;
  return `${hours} h${rest ? ` ${rest} min` : ""}`;
};

/** "Fri 20 Mar" */
const shortDay = (date) => (date ? calendarShortDay(String(date).slice(0, 10)) : "");

/**
 * A holiday line as the review shows it. Accepts a stored line (`GET …/holiday-lines`, which has an id and can be
 * reviewed) or the line the input review derives from current facts (no id).
 */
/**
 * APP-HR-102 (Payroll Gate 3): what the payslip already pays for a holiday day under review, so HR enters only what
 * the review adds and never pays overtime twice.
 */
export function holidayAlreadyPaidText(line = {}) {
  const worked = Number(line.workedMinutes || 0) > 0;
  const overtime = Number(line.overtimeMinutes || 0) > 0;
  if (worked && overtime) {
    return "Already on this payslip for this day: regular pay for the time worked, and the approved overtime at the employee's own overtime rate. Enter only what the holiday adds on top of both; 0 if nothing is added.";
  }
  if (worked) return "Already on this payslip for this day: regular pay for the time worked. Enter only what the holiday adds on top; 0 if nothing is added.";
  return "Not worked: a daily or hourly wage pays nothing else for this day, and a salary already includes it. Enter what this day adds to the payslip; 0 if nothing is added.";
}

export function holidayLineView(line) {
  const stored = Array.isArray(line.observances);
  const names = stored ? line.observances.map((item) => item.name) : [];
  const classes = stored ? line.observances.map((item) => item.dayClass) : (line.dayClasses || []);
  const closure = line.closure || null;
  const title = stored
    ? (names.length ? names.join(" and ") : closure?.name || "Holiday")
    : (line.name || closure?.name || "Holiday");
  const kind = classes.length > 1 ? "More than one official holiday"
    : classes.length === 1 ? CLASS_WORD[classes[0]] || "Official holiday"
      : closure ? "Closed by your business" : "Holiday";
  const worked = Number(line.workedMinutes || 0) > 0;
  const facts = [
    worked ? `Worked ${duration(line.workedMinutes)}` : "Not worked",
    Number(line.overtimeMinutes || 0) > 0 ? `${duration(line.overtimeMinutes)} of approved overtime` : null,
    line.restDay === "YES" ? "rest day" : null,
    classes.length && closure ? `business closed (${closure.name})` : null,
  ].filter(Boolean).join(" · ");
  const state = line.state === "REVIEW_REQUIRED" ? "review" : line.state === "MANUALLY_REVIEWED" ? "reviewed" : "auto";
  const basis = stored ? line.reference?.basis : line.referenceBasis;
  const referenceCents = stored ? line.reference?.amountCents : line.referenceCents;
  // The server sets the basis: a statutory minimum (A) may not be undercut; B is information only.
  const reference = referenceCents == null || !basis || basis === "NONE" ? null : {
    cents: Number(referenceCents),
    isMinimum: basis === "STATUTORY_MINIMUM",
    label: basis === "STATUTORY_MINIMUM" ? "Government minimum" : "For reference",
  };
  return {
    id: stored ? line.id : null,
    memberId: line.memberId ?? null,
    date: line.date,
    dateText: shortDay(line.date),
    title,
    kind,
    facts,
    worked,
    state,
    reason: line.reasonText || "",
    handlingNotSet: stored ? line.handling === "NOT_SET" : /is not set/.test(line.reasonText || ""),
    reference,
    amountCents: line.amountCents == null ? null : Number(line.amountCents),
    review: stored ? line.review || null : null,
    statutory: holidayStatutoryText(line.statutoryOutcome, line.statutoryEvidence),
  };
}

const KIND_WORD = { RETAIL_OR_SERVICE: "retail or service business", OTHER: "business that is not retail or service" };

/**
 * Report 58 (M1): what the law required of the business for this holiday, as payroll decided it and froze it with the
 * line. The server decides; this only says it. null when nothing was decided (older lines, closures).
 */
export function holidayStatutoryText(outcome, evidence) {
  if (!outcome) return null;
  const size = evidence?.regularWorkers != null
    ? `${KIND_WORD[evidence.kind] || "business"} with ${evidence.regularWorkers} worker${Number(evidence.regularWorkers) === 1 ? "" : "s"}${evidence.kind === "RETAIL_OR_SERVICE" && evidence.threshold != null ? ` (exception only below ${evidence.threshold})` : ""}`
    : null;
  switch (outcome) {
  case "APPLICABLE": return size ? `Required by law: a ${size}.` : "Required by law.";
  case "MORE_FAVOURABLE_PRACTICE": return `Not required by law for a ${size || "small retail or service business"}; the owner recorded that the business pays holiday pay anyway, so the standard holiday rules apply.`;
  case "NOT_REQUIRED_LAW_ONLY": return `Not required by law for a ${size || "small retail or service business"}, and the owner recorded that the business pays only what the law requires.`;
  case "ENTITLEMENT_UNDECIDED": return `Not required by law for a ${size || "small retail or service business"}; the owner has not said whether the business pays it anyway, so a person decides.`;
  case "FACTS_MISSING": return "The business size is not confirmed; the standard holiday rules were applied.";
  case "FACTS_CHANGED": return "The business size must be confirmed again; the standard holiday rules were applied.";
  case "FACTS_CONFLICT": return "The recorded business size needs correcting; the standard holiday rules were applied.";
  default: return null;
  }
}

/** "1,234.50" / "1234.5" → cents, or null when it is not an amount of ₱0.00 or more with at most two decimals. */
export function pesosToCents(value) {
  const text = String(value ?? "").replace(/[₱,\s]/g, "");
  if (!/^\d{1,9}(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

export const HOLIDAY_REVIEW_LIMITS = { maxCents: 100_000_000, reasonMin: 5, reasonMax: 500 };

/** Client-side checks that mirror the server's. The minimum is checked by the server, which owns the basis. */
export function holidayReviewErrors({ amount, reason }) {
  const errors = {};
  const cents = pesosToCents(amount);
  if (String(amount ?? "").trim() === "") errors.amount = "Enter the amount for this day. Enter 0 if nothing is added.";
  else if (cents == null) errors.amount = "Enter an amount in pesos, for example 600 or 802.50.";
  else if (cents > HOLIDAY_REVIEW_LIMITS.maxCents) errors.amount = "That amount is too large. Check the figure.";
  const text = String(reason || "").trim();
  if (text.length < HOLIDAY_REVIEW_LIMITS.reasonMin) errors.reason = "Say how you arrived at this amount (at least 5 characters).";
  else if (text.length > HOLIDAY_REVIEW_LIMITS.reasonMax) errors.reason = `Keep the reason to ${HOLIDAY_REVIEW_LIMITS.reasonMax} characters.`;
  return { errors, cents, reason: text };
}
